/**
 * NotificationPreferencesView — Simplified per-category notification preferences.
 *
 * Wired to PUT /notifications/preferences (relational table).
 * Per notification TYPE with E-Mail / Push sub-toggles.
 * Replaces both the JSONB 4-toggle system and the full channel×type matrix.
 */

import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import useNotificationPreferences from '../../hooks/useNotificationPreferences';
import { AnimatedPage } from '../../components/motion';

// All notification categories — each maps to its parent app's feature flag.
// null featureKey = always shown (Phase-1 app is live).
const ALL_NOTIFICATION_CATEGORIES = [
  { type: 'SUBSIDY_DEADLINE', icon: '📋', featureKey: null },
  { type: 'WEATHER_ALERT', icon: '🌤️', featureKey: null },
  { type: 'SCOUTING_HIGH_SEVERITY', icon: '🔍', featureKey: null },
  { type: 'CONTRACT_EXPIRY', icon: '📄', featureKey: 'contracts_app' },
  { type: 'INSURANCE_EXPIRY', icon: '🛡️', featureKey: 'insurance_app' },
  { type: 'CASHFLOW_GAP', icon: '💰', featureKey: 'cashflow_app' },
  { type: 'LOW_STOCK', icon: '📦', featureKey: 'stock_app' },
  { type: 'COVERAGE_GAP', icon: '⚠️', featureKey: 'insurance_app' },
];

const CHANNELS = ['email', 'push'];

function Toggle({ checked, onChange, disabled, testId }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={onChange}
      disabled={disabled}
      data-testid={testId}
      className={`relative w-10 h-6 rounded-full transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center ${
        disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'
      }`}
    >
      <span className={`block w-10 h-6 rounded-full transition-colors ${
        checked ? 'bg-[var(--aif-color-primary)]' : 'bg-[var(--aif-bg-elevated)]'
      }`}>
        <span className={`block w-5 h-5 bg-white rounded-full shadow transition-transform mt-0.5 ${
          checked ? 'translate-x-[18px] ml-0.5' : 'translate-x-0 ml-0.5'
        }`} />
      </span>
    </button>
  );
}

export default function NotificationPreferencesView({ onClose, isFeatureEnabled }) {
  const { t } = useTranslation();
  const { preferences, defaults, isLoading, savePreferences } = useNotificationPreferences();
  const [draftPrefs, setDraftPrefs] = useState({});
  const [saveStatus, setSaveStatus] = useState(null);

  // Phase-gate: only show notification types whose parent app is live
  const visibleCategories = ALL_NOTIFICATION_CATEGORIES.filter(
    cat => !cat.featureKey || isFeatureEnabled?.(cat.featureKey)
  );

  // Merge server prefs + defaults into draft
  useEffect(() => {
    if (isLoading) return;
    const map = {};

    // Start from defaults
    if (defaults?.channel_defaults) {
      for (const [type, chans] of Object.entries(defaults.channel_defaults)) {
        map[type] = {};
        for (const ch of CHANNELS) {
          map[type][ch] = chans[ch] ?? false;
        }
      }
    }

    // Ensure all categories exist
    for (const cat of ALL_NOTIFICATION_CATEGORIES) {
      if (!map[cat.type]) {
        map[cat.type] = { email: false, push: false };
      }
    }

    // Override with actual user prefs
    preferences.forEach(p => {
      if (!map[p.notification_type]) map[p.notification_type] = {};
      if (CHANNELS.includes(p.channel)) {
        map[p.notification_type][p.channel] = p.enabled;
      }
    });

    setDraftPrefs(map);
  }, [preferences, defaults, isLoading]);

  const toggleChannel = useCallback((type, channel) => {
    setDraftPrefs(prev => ({
      ...prev,
      [type]: {
        ...prev[type],
        [channel]: !(prev[type]?.[channel] ?? false),
      },
    }));
  }, []);

  const handleSave = async () => {
    setSaveStatus('saving');
    const flat = [];
    for (const cat of ALL_NOTIFICATION_CATEGORIES) {
      const chans = draftPrefs[cat.type] || {};
      for (const ch of CHANNELS) {
        flat.push({
          notification_type: cat.type,
          channel: ch,
          enabled: chans[ch] ?? false,
          quiet_hours_start: null,
          quiet_hours_end: null,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/Berlin',
          dedup_window_minutes: null,
        });
      }
      // Always keep in_app enabled
      flat.push({
        notification_type: cat.type,
        channel: 'in_app',
        enabled: true,
        quiet_hours_start: null,
        quiet_hours_end: null,
        timezone: 'Europe/Berlin',
        dedup_window_minutes: null,
      });
    }
    const ok = await savePreferences(flat);
    setSaveStatus(ok ? 'saved' : 'error');
    setTimeout(() => setSaveStatus(null), 3000);
  };

  if (isLoading) {
    return (
      <AnimatedPage className="fixed inset-0 z-[var(--aif-z-page-fullscreen)] bg-[var(--aif-bg-page)] flex flex-col">
        <Header onClose={onClose} t={t} />
        <div className="flex-1 flex items-center justify-center">
          <span className="text-sm text-[var(--aif-text-muted)]">{t('notification.loading', 'Wird geladen ...')}</span>
        </div>
      </AnimatedPage>
    );
  }

  return (
    <AnimatedPage className="fixed inset-0 z-[var(--aif-z-page-fullscreen)] bg-[var(--aif-bg-page)] flex flex-col">
      <Header onClose={onClose} t={t} />

      <div className="flex-1 overflow-y-auto" data-testid="notification-preferences-view">
        <div className="max-w-lg mx-auto px-4 py-6 space-y-4">
          <p className="text-sm text-[var(--aif-text-muted)]">
            {t('notificationPrefs.subtitle', 'Wähle pro Kategorie, ob du per E-Mail oder Push benachrichtigt werden möchtest. In-App-Benachrichtigungen sind immer aktiv.')}
          </p>

          {/* Channel header labels */}
          <div className="flex items-center justify-end gap-6 pr-1 pb-1">
            <span className="text-[11px] font-medium text-[var(--aif-text-muted)] w-11 text-center">
              {t('notification.channel.email', 'E-Mail')}
            </span>
            <span className="text-[11px] font-medium text-[var(--aif-text-muted)] w-11 text-center opacity-50">
              {t('notification.channel.push', 'Push')}
            </span>
          </div>

          {/* Category rows */}
          <div className="space-y-1">
            {visibleCategories.map(cat => {
              const chans = draftPrefs[cat.type] || {};
              return (
                <div
                  key={cat.type}
                  className="flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-[var(--aif-bg-surface)] transition"
                  data-testid={`notif-row-${cat.type}`}
                >
                  <span className="text-lg flex-shrink-0">{cat.icon}</span>
                  <span className="text-sm text-[var(--aif-text-primary)] flex-1">
                    {t(`notification.type_label.${cat.type}`, cat.type)}
                  </span>
                  <div className="flex items-center gap-6">
                    <Toggle
                      checked={chans.email ?? false}
                      onChange={() => toggleChannel(cat.type, 'email')}
                      testId={`pref-${cat.type}-email`}
                    />
                    <Toggle
                      checked={chans.push ?? false}
                      onChange={() => toggleChannel(cat.type, 'push')}
                      disabled
                      testId={`pref-${cat.type}-push`}
                    />
                  </div>
                </div>
              );
            })}
          </div>

          <p className="text-xs text-[var(--aif-text-muted)] px-3">
            {t('notification.settings.push_note', 'Push-Benachrichtigungen werden in einer späteren Version aktiviert.')}
          </p>

          {/* Save button */}
          <div className="pt-2">
            <button
              type="button"
              onClick={handleSave}
              disabled={saveStatus === 'saving'}
              data-testid="notif-prefs-save"
              className="w-full py-3 text-sm font-medium rounded-2xl transition bg-[var(--aif-color-primary)] text-white hover:opacity-90 disabled:opacity-50 min-h-[44px]"
            >
              {saveStatus === 'saving'
                ? t('notification.settings.saving', 'Wird gespeichert ...')
                : t('notification.settings.save', 'Speichern')}
            </button>
            {saveStatus === 'saved' && (
              <p className="text-sm text-[var(--aif-color-success)] text-center mt-2">{t('notification.settings.saved', 'Gespeichert')}</p>
            )}
            {saveStatus === 'error' && (
              <p className="text-sm text-[var(--aif-color-danger)] text-center mt-2">{t('notification.settings.save_error', 'Fehler beim Speichern')}</p>
            )}
          </div>
        </div>
      </div>
    </AnimatedPage>
  );
}

function Header({ onClose, t }) {
  return (
    <header className="flex items-center gap-3 px-4 py-3 border-b border-[var(--aif-border-default)] flex-shrink-0">
      <button onClick={onClose} className="w-10 h-10 flex items-center justify-center rounded-xl hover:bg-[var(--aif-bg-surface)] transition text-[var(--aif-text-muted)]">
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15.75 19.5L8.25 12l7.5-7.5" /></svg>
      </button>
      <span className="text-sm font-semibold text-[var(--aif-text-primary)]">
        {t('settings.notifications', 'Benachrichtigungen')}
      </span>
    </header>
  );
}
